/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useMemo, useRef, useEffect } from "react";
import { type FilterColumn, type FilterConditionEntry } from "@CubeComponents/html/SlickGrid1/Modals/FilterButton";
import {
  getConditionMet,
  applyLogicToResult,
  collectAncestorIds,
  type FilterType,
  type QueryOperator,
} from "@CubeComponents/html/SlickGrid1/filterUtils";
import { ColumnData } from "@WebTypes/scheduleDomain";
// The Gantt library's own column type — `gantt.config.columns` is typed with this one, not the
// look-alike in `@WebTypes/scheduleDomain`.
import type { GanttStatic, GridColumn, Task } from "./dhtmlxgantt";

declare const gantt: GanttStatic;

const GANTT_COLUMN_FILTER_TYPE_MAP: Record<string, string> = {
  TaskDisp: "Text",
  text: "Text",
  start_date: "Date",
  end_date: "Date",
  duration: "Number",
  actualStartDate: "Date",
  actualEndDate: "Date",
  plannedStartDate: "Date",
  plannedEndDate: "Date",
  plannedDuration: "Number",
  remDuration: "Number",
  progress: "Number",
  totalSlack: "Number",
  critical: "Text",
  "planned%": "Number",
  startVariance: "Number",
  finishVariance: "Number",
  predecessor: "Text",
  successor: "Text",
};

const computeNumericFromTemplate = (task: Task, field: string): number | null => {
  const column = gantt.config.columns?.find((c: GridColumn) => c.name === field);
  if (!column?.template) return task[field] ?? null;
  const rendered = column.template(task);
  if (rendered === undefined || rendered === null || rendered === "") return null;
  const numericValue = parseFloat(String(rendered).replace("%", "").trim());
  return Number.isNaN(numericValue) ? null : numericValue;
};

export const getGanttTaskFieldValue = (task: Task, field: string): unknown => {
  switch (field) {
    case "start_date":
    case "end_date":
    case "actualStartDate":
    case "actualEndDate":
    case "plannedStartDate":
    case "plannedEndDate":
      return task[field] ? new Date(task[field]).getTime() : null;
    case "progress":
      return task.progress != null ? Math.round(task.progress * 100) : null;
    case "critical":
      if (task.critical) return task.critical === "1" ? "Yes" : "No";
      return gantt.isCriticalTask(task) ? "Yes" : "No";
    case "remDuration":
    case "totalSlack":
    case "planned%":
    case "startVariance":
    case "finishVariance":
    case "plannedDuration":
    case "duration":
      return computeNumericFromTemplate(task, field);
    case "predecessor": {
      const preds = (task.$target || []).map((linkId: string | number) => {
        const link = gantt.getLink(linkId);
        const predTask = gantt.getTask(link.source);
        return predTask?.TaskDisp;
      });
      return preds.join(", ");
    }
    case "successor": {
      const succs = (task.$source || []).map((linkId: string | number) => {
        const link = gantt.getLink(linkId);
        const succTask = gantt.getTask(link.target);
        return succTask?.TaskDisp;
      });
      return succs.join(", ");
    }
    default:
      return task[field];
  }
};

interface UseGanttFiltersArgs {
  ganttDataAvailable: boolean;
  savedColumns: ColumnData[];
  rerenderGantt: boolean | null;
}

interface UseGanttFiltersReturn {
  ganttFilterColumns: FilterColumn[];
  ganttTasksDataset: Task[];
  handleGanttFilterChange: (filterConditionsObj: Record<string, FilterConditionEntry>) => void;
  getGanttTaskFieldValue: typeof getGanttTaskFieldValue;
}

export const useGanttFilters = ({
  ganttDataAvailable,
  savedColumns,
  rerenderGantt,
}: UseGanttFiltersArgs): UseGanttFiltersReturn => {
  const ganttFilterEventId = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (ganttFilterEventId.current) {
        gantt.detachEvent(ganttFilterEventId.current);
        ganttFilterEventId.current = null;
      }
    };
  }, []);

  const ganttFilterColumns: FilterColumn[] = useMemo(() => {
    return (
      gantt.config.columns
        ?.filter((col: GridColumn) => col.name && col.name !== "add" && col.name !== "CustomIcon" && !col.hide)
        .map((col: GridColumn) => ({
          field: col.name as string,
          name: (col.label || (col.name as string)) as string,
          filterType: GANTT_COLUMN_FILTER_TYPE_MAP[col.name as string] || "Text",
          id: col.name as string,
        })) || []
    );
  }, [ganttDataAvailable, savedColumns, rerenderGantt]);

  const ganttTasksDataset = useMemo(() => {
    try {
      return gantt.getTaskByTime?.() || [];
    } catch {
      return [];
    }
  }, [ganttDataAvailable]);

  const handleGanttFilterChange = (filterConditionsObj: Record<string, FilterConditionEntry>) => {
    if (ganttFilterEventId.current) {
      gantt.detachEvent(ganttFilterEventId.current);
      ganttFilterEventId.current = null;
    }

    const conditionKeys = Object.keys(filterConditionsObj);

    if (conditionKeys.length > 0) {
      const matchedIds: (string | number)[] = [];
      gantt.eachTask((task: Task) => {
        let result = false;
        for (let i = 0; i < conditionKeys.length; i++) {
          // Both come straight from `Object.keys` of the same object, so the lookup and the `id` the
          // filter modal always writes are present. Erased assertions.
          const condition = filterConditionsObj[conditionKeys[i]!]!;
          const fieldValue = getGanttTaskFieldValue(task, condition.id!);
          const conditionMet = getConditionMet(
            condition.type as FilterType,
            fieldValue,
            condition.option as QueryOperator,
            condition.text,
          );

          if (i === 0) {
            result = conditionMet;
          } else {
            result = applyLogicToResult(result, conditionMet, condition.logic?.toLowerCase() || "and");
          }
        }
        if (result) matchedIds.push(task.id);
      });

      const matchSet = collectAncestorIds(matchedIds, (id) => {
        if (!gantt.isTaskExists(id)) return null;
        const parentId = gantt.getTask(id).parent;
        return parentId && parentId !== 0 ? parentId : null;
      });

      ganttFilterEventId.current = gantt.attachEvent(
        "onBeforeTaskDisplay",
        function (_id: string | number, task: Task) {
          return matchSet.has(task.id);
        },
      );
    }

    gantt.render();
  };

  return { ganttFilterColumns, ganttTasksDataset, handleGanttFilterChange, getGanttTaskFieldValue };
};
